import { useCaptureAnnotationList } from "@/hooks/use-capture-annotations";
import { useGoToAnnotation } from "@/hooks/use-go-to-annotation";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useAppSelector } from "@/store/store-hooks";
import { selectChildDepthFirst } from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import {
  isIElementExternalMarkup,
  isIElementMarkup,
  MarkupIElement,
} from "@faro-lotv/ielement-types";
import { isEqual } from "es-toolkit";
import { useCallback } from "react";

/** An annotation taken inside a capture, as listed in the waypoint previews */
export type CaptureAnnotationItem = {
  /** Id of the annotation */
  id: GUID;
  /** Title shown to the user */
  title: string;
  /** The markup to go to, undefined for action objects (e.g. image or PDF) */
  markup?: MarkupIElement;
};

/**
 * @param capture The capture to list the annotations of
 * @param openCapture Called when an annotation without markup is clicked, to open the capture it belongs to
 * @returns the annotations of the capture, and the callback to go to one of them
 */
export function useCaptureAnnotationItems(
  capture: IElementWaypoint | undefined,
  openCapture: () => void,
): {
  annotations?: CaptureAnnotationItem[];
  onAnnotationClick(id: GUID): void;
} {
  const captureAnnotations = useCaptureAnnotationList(capture);
  const annotations = useAppSelector(
    (state) =>
      captureAnnotations.map((annotation) => {
        const markup = selectChildDepthFirst(
          annotation,
          (el): el is MarkupIElement =>
            isIElementMarkup(el) || isIElementExternalMarkup(el),
        )(state);
        return {
          id: annotation.id,
          title: markup?.name ?? annotation.name,
          markup,
        };
      }),
    isEqual,
  );

  const goToAnnotation = useGoToAnnotation();
  const onAnnotationClick = useCallback(
    (id: GUID) => {
      const markup = annotations.find((a) => a.id === id)?.markup;
      if (markup) {
        goToAnnotation(markup);
      } else {
        openCapture();
      }
    },
    [annotations, goToAnnotation, openCapture],
  );

  return { annotations, onAnnotationClick };
}
